import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

// material-ui
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project imports
import NavItem from './NavItem';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';

// assets
import { IconExpandMore } from 'utils/icons';

// ==============================|| NAVIGATION - LIST COLLAPSE (ACCORDION) ||============================== //
// Mantis free's own NavGroup.jsx explicitly punts on type:'collapse'
// ("only available in paid version") - this is our own real implementation
// (plain MUI Collapse), not a Pro-feature workaround.
//
// "open" stav vlastní rodič NavGroup (2026-08-23 - vzájemné vyloučení mezi
// sourozeneckými collapse položkami, "Správce"/"Dokumentace" šly dřív mít
// otevřené obě najednou) - sem chodí jen jako `expanded`/`onToggle` props,
// `expanded` (2026-08-30, real bug fixed - viz níže) je teď JEDINÝ zdroj
// pravdy, bez vlastního "isActiveRoute" OR.
//
// Vnořený subaccordion (2026-08-30, na výslovné přání uživatele - "Docházkový
// systém... přesun do accordion header Manažeři jako další subaccordion") -
// `item.children` teď může obsahovat i položku type:'collapse', ne jen
// type:'item'. Na rozdíl od TOP-level collapse položek (jejichž vzájemné
// vyloučení řídí NavGroup) si vnořený subaccordion drží OTEVŘENO/ZAVŘENO
// jen sám pro sebe (`nestedExpandedId` níže) - stejný vzájemně-výhradní
// princip jako NavGroup, jen o úroveň níž.
//
// Bug fix (2026-08-30, na výslovné přání uživatele - "oprav aby se u
// accordionu v menu pokud se otevře jiný zavřel původní") - `expanded`
// dřív bylo `expandedProp || isActiveRoute`, počítané NEZÁVISLE na
// `expandedProp`. Klik na JINÝ accordion sice přepnul `expandedProp` na
// false, ale `isActiveRoute` (stály jsem na stránce uvnitř tohohle
// accordionu) ho stejně držel otevřený - dva nezávislé zdroje pravdy bez
// vzájemné výhradnosti. Teď aktivní-routa jen SEEDUJE/aktualizuje
// `expandedCollapseId`/`nestedExpandedId` PŘI NAVIGACI (efekt níže, stejný
// vzor jako NavGroup.jsx), ruční klik mezi navigacemi si drží plnou
// kontrolu.
export default function NavCollapse({ item, level, expanded: expandedProp, onToggle, isNested = false }) {
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const location = useLocation();
  const [nestedExpandedId, setNestedExpandedId] = useState(() => findActiveChildCollapseId(item.children, location.pathname));

  useEffect(() => {
    const activeId = findActiveChildCollapseId(item.children, location.pathname);
    if (activeId) setNestedExpandedId(activeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const expanded = expandedProp;
  const Icon = item.icon;

  if (!drawerOpen) {
    return (
      <Box sx={{ position: 'relative' }}>
        <ListItemButton
          onClick={() => handlerDrawerOpen(true)}
          sx={{ pl: 1.5, py: 1.25, '&:hover': { bgcolor: 'transparent' } }}
        >
          <ListItemIcon
            sx={{
              minWidth: 28,
              color: 'text.primary',
              borderRadius: 1.5,
              width: 36,
              height: 36,
              alignItems: 'center',
              justifyContent: 'center',
              // Rozbalená (open===true) položka zůstává vizuálně
              // identifikovatelná i v úzkém sidebaru - accordion sám se v
              // téhle úzké podobě nevykresluje (viz early return níže), ale
              // "open" stav si appka pamatuje, takže si uživatel může
              // ikonu spojit s tím, co měl naposledy rozbalené (2026-08-22,
              // na přání uživatele).
              bgcolor: expanded ? 'secondary.lighter' : 'transparent',
              '&:hover': { bgcolor: 'secondary.lighter' }
            }}
          >
            {Icon && <Icon style={{ fontSize: 26, stroke: '1.5' }} />}
          </ListItemIcon>
        </ListItemButton>
      </Box>
    );
  }

  return (
    <>
      <ListItemButton onClick={onToggle} sx={{ pl: `${level * 28}px`, py: 1 }}>
        {Icon && (
          // minWidth 40 (2026-08-30, real bug fixed - "zarovnat ikony") -
          // dřív 44, o 4px víc než NavItem's vlastní ikonový sloupec
          // (minWidth 40 při otevřeném drawer, viz NavItem.jsx). Neviditelné,
          // dokud vedle sebe stály jen SOUROZENECKÉ collapse headery (Manažeři/
          // Správce/Dokumentace, všechny stejně 44) - jakmile ale vnořený
          // subaccordion (Docházkový systém) sedí ve stejném sloupci vedle
          // obyčejných odkazů (Push zprávy), ten rozdíl posunul text mimo
          // společný sloupec.
          <ListItemIcon sx={{ minWidth: 40, color: 'text.primary' }}>
            <Icon style={{ fontSize: '1.6rem' }} />
          </ListItemIcon>
        )}
        <ListItemText primary={<Typography variant={isNested ? 'body2' : 'h6'}>{item.title}</Typography>} />
        <IconExpandMore
          style={{
            fontSize: isNested ? '0.9rem' : '1.1rem',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s'
          }}
        />
      </ListItemButton>
      {/* 2026-08-30, na výslovné přání uživatele ("odkazy v accordion body
          jsou odskočené, srovnej je tak jak jsou jejich header") - `level`
          (ne `level + 1`) drží ODKAZY (NavItem) i VNOŘENÝ SUBACCORDION
          (NavCollapse) na stejném odsazení jako jejich vlastní header -
          "Docházkový systém" musí být zarovnaný vlevo přesně jako "Push
          zprávy", žádný odskok (na výslovné přání uživatele, 2026-08-30:
          "accordion header Docházkový systém má být zarovnaný vlevo jako
          odkaz Push Zprávy, nemá mít žádný odskok"). Vizuální odlišení
          "tohle je subaccordion, ne obyčejný odkaz" dává vlastní
          IconExpandMore vpravo (viz výš), ne odsazení.
          Tělo VNOŘENÉHO subaccordionu (isNested) má vlastní odlišení
          (2026-08-30, na výslovné přání uživatele - "u vnitř accordion
          Docházkový systém v body zmenši písmo odkazu a změň i background
          na background stránky ať se rozliší body subaccordion") -
          `background.default` (barva stránky, ne menu) místo
          `secondary.lighter`. Tělo TOP-level collapse (Manažeři/Správce/
          Dokumentace) zůstává `secondary.lighter` beze změny - `action.hover`
          byl na tmavém pozadí příliš slabý. */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <List component="div" disablePadding sx={{ bgcolor: isNested ? 'background.default' : 'secondary.lighter' }}>
          {item.children?.map((child) => {
            if (child.type === 'collapse') {
              return (
                <NavCollapse
                  key={child.id}
                  item={child}
                  level={level}
                  expanded={nestedExpandedId === child.id}
                  onToggle={() => setNestedExpandedId((prev) => (prev === child.id ? null : child.id))}
                  isNested
                />
              );
            }
            // "linie uprostřed [role]" (2026-09-01, na výslovné přání
            // uživatele) - popisný oddělovač PŘED odkazem, ne klikatelná
            // položka. Zatím jediné použití je buildAttendanceDashboardMenu()
            // (menu-items/attendance.jsx). Linie i text v aktivní barvě
            // (primary.main, 2026-09-01, na výslovné přání uživatele) -
            // stejná barva jako aktivní odkaz/záložka/accordion jinde v
            // appce, ne tlumená text.secondary.
            if (child.type === 'divider') {
              return (
                <Divider
                  key={child.id}
                  textAlign="center"
                  sx={{ mx: 2, my: 1, '&::before, &::after': { borderColor: 'primary.main' } }}
                >
                  <Typography variant="caption" sx={{ color: 'primary.main', textTransform: 'uppercase', fontSize: '0.7rem' }}>
                    {child.title}
                  </Typography>
                </Divider>
              );
            }
            return <NavItem key={child.id} item={child} level={level} />;
          })}
        </List>
      </Collapse>
    </>
  );
}

// Rekurzivní (2026-08-30) - stejný vzor jako NavGroup.jsx's vlastní
// findActiveCollapseId(), jen nad `item.children` JEDNOHO collapse (jeho
// vlastní vnořené subaccordiony), ne nad celou group.
function containsRoute(children, pathname) {
  return (
    children?.some((child) => child.url === pathname || (child.type === 'collapse' && containsRoute(child.children, pathname))) ?? false
  );
}

function findActiveChildCollapseId(children, pathname) {
  for (const child of children || []) {
    if (child.type === 'collapse' && containsRoute(child.children, pathname)) {
      return child.id;
    }
  }
  return null;
}

NavCollapse.propTypes = {
  item: PropTypes.any,
  level: PropTypes.number,
  expanded: PropTypes.bool,
  onToggle: PropTypes.func,
  isNested: PropTypes.bool
};
